import { passwordMinimumLength } from "@cem/contracts";
import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Button } from "@cem/ui";
import { ApiRequestError } from "./api.js";
import { useAuth } from "./auth.js";

export const PasswordChangePage = ({
  mobile = false,
}: {
  mobile?: boolean;
}) => {
  const auth = useAuth();
  const minimum = passwordMinimumLength(auth.user?.permissions ?? []);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const keyboardState = (event: KeyboardEvent<HTMLInputElement>): void =>
    setCapsLock(event.getModifierState("CapsLock"));

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setReference(null);
    if (newPassword !== confirmation) {
      setError("Le due nuove password non coincidono.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("La nuova password deve essere diversa da quella temporanea.");
      return;
    }
    setSubmitting(true);
    try {
      await auth.changePassword(currentPassword, newPassword);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Il cambio della password non è riuscito.",
      );
      setReference(
        cause instanceof ApiRequestError ? (cause.correlationId ?? null) : null,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="password-change-title">
        <img className="login-logo" src="/cem-logo.png" alt="CEM" />
        <p className="eyebrow">SICUREZZA ACCOUNT</p>
        <h1 id="password-change-title">Crea la tua password</h1>
        <p className="login-intro">
          La password ricevuta è temporanea. Impostane una nuova prima di{" "}
          {mobile ? "entrare in App CEM." : "entrare nel gestionale."}
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Password temporanea
            <input
              autoComplete="current-password"
              minLength={minimum}
              onChange={(event) => setCurrentPassword(event.target.value)}
              onKeyDown={keyboardState}
              onKeyUp={keyboardState}
              required
              type={showPasswords ? "text" : "password"}
              value={currentPassword}
            />
          </label>
          <label>
            Nuova password
            <input
              autoComplete="new-password"
              minLength={minimum}
              onChange={(event) => setNewPassword(event.target.value)}
              onKeyDown={keyboardState}
              onKeyUp={keyboardState}
              required
              type={showPasswords ? "text" : "password"}
              value={newPassword}
            />
          </label>
          <label>
            Ripeti la nuova password
            <input
              autoComplete="new-password"
              minLength={minimum}
              onChange={(event) => setConfirmation(event.target.value)}
              onKeyDown={keyboardState}
              onKeyUp={keyboardState}
              required
              type={showPasswords ? "text" : "password"}
              value={confirmation}
            />
          </label>
          <label className="password-toggle">
            <input
              checked={showPasswords}
              onChange={(event) => setShowPasswords(event.target.checked)}
              type="checkbox"
            />
            Mostra le password
          </label>
          {capsLock ? (
            <p className="caps-lock" role="status">
              Bloc Maiusc è attivo
            </p>
          ) : null}
          {error === null ? null : (
            <p className="form-error" role="alert">
              {error}
              {reference === null ? null : (
                <small>Riferimento: {reference}</small>
              )}
            </p>
          )}
          <Button disabled={submitting} tone="primary" type="submit">
            {submitting ? "Salvataggio…" : "Salva e accedi"}
          </Button>
        </form>
        <p className="phase-note">La password non viene mostrata nei log</p>
      </section>
      <aside className="login-scene" aria-hidden="true">
        <div className="scene-grid" />
        <div className="scene-copy">
          <span>Accesso sicuro.</span>
          <span>Lavoro protetto.</span>
        </div>
      </aside>
    </main>
  );
};
